import { FC, useEffect, useMemo } from 'react';

import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import { useFormik } from 'formik';
import { CHILD_PROTECTION_VALIDATION_SCHEMA } from 'components/ChildProtection/validationSchema';
import { ChildCollectionEnum, mapServerError } from 'core';
import { ChildDetails, ChildProtection, DEFAULT_GUARDIAN, ServerErrors } from 'components';
import { useGetChildDetailsQuery, useUpdateChildDetailsMutation } from 'store/my-registrations/myRegistrationsAPI';

import { useFormFields, useServerErrors } from '@britishcouncil/react-forms';
import { Button } from '@britishcouncil/react-solas';
import { ApiErrorPage, Modal } from '@britishcouncil/react-solas-ors3';

export const ChildProtectionModal: FC<{ registrationGroupId: number; readonly: boolean; onExit: () => void }> = ({
  registrationGroupId,
  readonly,
  onExit,
}) => {
  const {
    data: childDetails,
    isFetching: childDetailsIsLoading,
    error: childDetailsError,
  } = useGetChildDetailsQuery(registrationGroupId, { refetchOnMountOrArgChange: true });

  const [
    updateChildDetails,
    { isLoading: submitting, isSuccess: submitted, error: submitError, isError: isSubmitError },
  ] = useUpdateChildDetailsMutation();

  const dateOfBirth = useMemo(
    () => (childDetails ? DateTime.fromISO(childDetails.dateOfBirth).toJSDate() : undefined),
    [childDetails]
  );
  const serverError = useMemo(() => (isSubmitError ? mapServerError(submitError) : []), [isSubmitError, submitError]);

  const formik = useFormik<ChildDetails>({
    enableReinitialize: true,
    validationSchema: CHILD_PROTECTION_VALIDATION_SCHEMA,
    initialValues: childDetails
      ? {
          ...childDetails,
          allowedPeople: childDetails.allowedPeople.length > 0 ? childDetails.allowedPeople : [DEFAULT_GUARDIAN],
        }
      : {
          allowedPeople: [DEFAULT_GUARDIAN],
          childCollectionOption: ChildCollectionEnum.ParentPickup,
          childStatementConfirmed: false,
        },
    onSubmit: (values) => {
      updateChildDetails({ registrationGroupId, childDetails: values });
    },
  });
  const fields = useFormFields(formik);
  const mainErrors = useServerErrors(formik, serverError);

  useEffect(() => {
    if (submitted) {
      onExit();
    }
  }, [onExit, submitted]);

  return (
    <Modal
      show={true}
      title="Child Protection Details"
      onExit={onExit}
      isLoading={childDetailsIsLoading}
      footer={
        <Footer>
          <Button intent="default" onClick={onExit} disabled={submitting}>
            Cancel
          </Button>
          <Button intent="primary" onClick={formik.submitForm} disabled={submitting || readonly} loading={submitting}>
            Save
          </Button>
        </Footer>
      }
    >
      <Container>
        {childDetailsError && <ApiErrorPage />}
        {childDetails && (
          <ChildProtection
            dateOfBirth={dateOfBirth}
            fields={fields}
            onGuardianAdd={(guardian) =>
              formik.setFieldValue('allowedPeople', [...formik.values.allowedPeople, guardian])
            }
            onGuardianRemove={(index) => {
              formik.setFieldValue(
                'allowedPeople',
                formik.values.allowedPeople.filter((x, i) => i !== index)
              );
            }}
          />
        )}
        {!!mainErrors && <ServerErrors errors={mainErrors} />}
      </Container>
    </Modal>
  );
};

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Container = styled.div`
  max-width: 632px;
`;
